import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import RedeemRoundedIcon from '@mui/icons-material/RedeemRounded'
import { useTimeline } from '../../context/useTimeline'
import type { TimelineElement } from '../../context/TimelineContext'

/**
 * Loot Drop Lanes — gamified side-scrolling quest road.
 *
 * Tasks + subtasks sorted chronologically by startDate render as waypoint
 * nodes along a horizontal lane, alternating above/below the central
 * connector. When a node's `progress` crosses <100 → 100 (observed via the
 * live `elements` snapshot), a chest animates down on that node's lane
 * position. Multiple concurrent completions all fan out in parallel —
 * each chest is anchored to its own node so there's no center-screen overlap.
 *
 * No `recordTrack` call from this view: GamificationContext's edge-detector
 * already fires TRACK_FINISH_TASK + spin rewards exactly once per
 * cross-client lock. This view is purely visual celebration on top of that.
 */

const ELIGIBLE_TYPES: ReadonlyArray<TimelineElement['type']> = ['TASK', 'SUBTASK']
const CHEST_VISIBLE_MS = 1800

/** Sort key: parseable startDate → ms; missing → +Infinity (trail). */
const sortKey = (raw: string | null | undefined): number => {
  if (!raw) return Number.POSITIVE_INFINITY
  const head = raw.length >= 10 ? raw.slice(0, 10) : raw
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return Number.POSITIVE_INFINITY
  const [y, m, d] = head.split('-').map(Number)
  return new Date(y, m - 1, d).getTime()
}

/** Walk parentId chain to find the root GOAL. */
const findRootGoalId = (
  el: TimelineElement,
  byId: Map<string, TimelineElement>,
): string | null => {
  if (el.type === 'GOAL') return el.id
  let cursor: TimelineElement | undefined = el
  while (cursor && cursor.type !== 'GOAL') {
    if (!cursor.parentId) return null
    cursor = byId.get(cursor.parentId)
  }
  return cursor?.id ?? null
}

interface NodeAugmentation {
  rootGoalId: string | null
  rootGoalComplete: boolean
}

interface ChestDrop {
  /** Unique key for AnimatePresence — `id-${timestamp}` lets the same node */
  /* re-trigger (mark complete → un-mark → mark again) without React keying clashes. */
  key: string
  nodeId: string
}

const NodeStone = ({ element }: { element: TimelineElement }) => {
  const isDone = (element.progress ?? 0) >= 100
  const percent = Math.max(0, Math.min(100, Math.round(element.progress ?? 0)))
  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-md select-none transition-colors duration-200 ${
          isDone
            ? 'border-2 border-[var(--success)] bg-[var(--success-soft)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))] text-xl'
            : 'border-2 border-[var(--accent)] bg-[var(--surface-muted)] text-[var(--text-strong)] text-[10px] font-semibold tabular-nums'
        }`}
        title={element.title}
      >
        {isDone ? <RedeemRoundedIcon fontSize="inherit" /> : `${percent}%`}
      </div>
      <div className="flex flex-col items-center max-w-[140px]">
        <span className="text-[9px] uppercase tracking-wider text-[var(--muted)] font-mono">
          {element.id.slice(0, 4).toUpperCase()}
        </span>
        <span className="text-[11px] text-[var(--text-strong)] font-semibold text-center leading-tight line-clamp-2">
          {element.title}
        </span>
      </div>
    </div>
  )
}

/**
 * Bouncing loot chest overlay anchored to a specific node. Lives inside the
 * node container and pops over it — no center-screen positioning, so multiple
 * concurrent drops naturally fan out across the lane.
 */
const LootChestOverlay = () => (
  <motion.div
    initial={{ y: -150, scale: 0.2, opacity: 0 }}
    animate={{ y: 0, scale: 1, opacity: 1 }}
    exit={{ y: -20, scale: 0.6, opacity: 0 }}
    transition={{ type: 'spring', bounce: 0.4, duration: 0.6 }}
    className="absolute -top-14 left-1/2 -translate-x-1/2 pointer-events-none z-40"
  >
    <div className="relative flex flex-col items-center">
      <div className="text-3xl text-[var(--success)]" /* mosaic-keep: icon */><RedeemRoundedIcon fontSize="inherit" /></div>
      <motion.span
        initial={{ opacity: 0, y: 0 }}
        animate={{ opacity: 1, y: -6 }}
        transition={{ delay: 0.25, duration: 0.4 }}
        className="text-[10px] font-bold uppercase tracking-wider text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))] mt-0.5"
      >
        +Loot
      </motion.span>
    </div>
  </motion.div>
)

export const LootDropLanesView = () => {
  const { elements } = useTimeline()

  // Chronological sort + filter — derived from raw elements every render.
  const lanes = useMemo(() => {
    const eligible = elements.filter((el) => ELIGIBLE_TYPES.includes(el.type))
    return [...eligible].sort((a, b) => sortKey(a.startDate) - sortKey(b.startDate))
  }, [elements])

  // Goal-completion lookup for connector coloring.
  const augmentations = useMemo<Map<string, NodeAugmentation>>(() => {
    const byId = new Map<string, TimelineElement>()
    elements.forEach((el) => byId.set(el.id, el))
    const result = new Map<string, NodeAugmentation>()
    for (const el of lanes) {
      const rootGoalId = findRootGoalId(el, byId)
      const rootGoal = rootGoalId ? byId.get(rootGoalId) : undefined
      result.set(el.id, {
        rootGoalId,
        rootGoalComplete: rootGoal ? (rootGoal.progress ?? 0) >= 100 : false,
      })
    }
    return result
  }, [elements, lanes])

  // ── Edge-detector: 0→100 transitions trigger chest drops ──────────────
  // Initialize from the FIRST render's snapshot so we don't fire chests for
  // every already-complete task on mount.
  const progressRef = useRef<Map<string, number> | null>(null)
  const [activeDrops, setActiveDrops] = useState<ChestDrop[]>([])

  useEffect(() => {
    const next = new Map<string, number>()
    for (const el of elements) next.set(el.id, el.progress ?? 0)
    const prev = progressRef.current
    if (prev !== null) {
      const fresh: ChestDrop[] = []
      const now = Date.now()
      for (const [id, current] of next) {
        const previous = prev.get(id) ?? current
        if (previous < 100 && current >= 100) {
          fresh.push({ key: `${id}-${now}`, nodeId: id })
        }
      }
      if (fresh.length > 0) {
        setActiveDrops((existing) => [...existing, ...fresh])
      }
    }
    progressRef.current = next
  }, [elements])

  // Auto-clear chests after their visible window. Single effect manages all
  // pending timeouts; cleanup cancels them on unmount.
  useEffect(() => {
    if (activeDrops.length === 0) return
    const timers = activeDrops.map((drop) =>
      window.setTimeout(() => {
        setActiveDrops((current) => current.filter((d) => d.key !== drop.key))
      }, CHEST_VISIBLE_MS),
    )
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [activeDrops])

  const dropsByNode = useMemo(() => {
    const map = new Map<string, ChestDrop[]>()
    for (const drop of activeDrops) {
      const bucket = map.get(drop.nodeId) ?? []
      bucket.push(drop)
      map.set(drop.nodeId, bucket)
    }
    return map
  }, [activeDrops])

  return (
    <div data-dashboard-horizontal-scroll="true" className="w-full h-full overflow-x-auto p-8 flex items-center relative [scrollbar-width:none] [&::-webkit-scrollbar]:hidden bg-[var(--surface-muted)]">
      {/* Empty-state */}
      {lanes.length === 0 ? (
        <div className="w-full text-center text-[var(--muted)] text-sm">
          No quests on the road. Schedule a task or subtask to begin the campaign.
        </div>
      ) : (
        <div className="relative flex items-center min-w-max">
          {/* Quest road — alternating-above/below nodes, segmented connector. */}
          {lanes.map((element, index) => {
            const isAbove = index % 2 === 0
            const nodeAug = augmentations.get(element.id)
            const nextEl = lanes[index + 1]
            const nextAug = nextEl ? augmentations.get(nextEl.id) : null
            // Connector segment to the NEXT node colors green when both
            // endpoints share the same fully-complete root goal.
            const segmentGreen =
              nodeAug?.rootGoalComplete &&
              nextAug?.rootGoalComplete &&
              nodeAug.rootGoalId !== null &&
              nodeAug.rootGoalId === nextAug.rootGoalId
            const drops = dropsByNode.get(element.id) ?? []

            return (
              <div
                key={element.id}
                className="flex flex-col items-center relative mx-12 justify-center h-48"
              >
                {/* Per-node connector segment to the right of this node.
                 * Renders only between nodes (skipped on last). */}
                {index < lanes.length - 1 && (
                  <div
                    className={`absolute top-1/2 left-1/2 -translate-y-1/2 border-t-4 border-dashed -z-10 ${
                      segmentGreen ? 'border-[var(--success)]/70' : 'border-[var(--border)]/40'
                    }`}
                    style={{ width: 'calc(100% + 6rem)' /* mx-12 on both sides = 6rem */ }}
                  />
                )}

                {/* Alternating stagger: even index above lane, odd below. */}
                <div className={isAbove ? 'mb-auto pt-2' : 'mt-auto pb-2'}>
                  <NodeStone element={element} />
                </div>

                {/* Chest drops fan out at this node's own anchor — never
                 * overlap with other concurrent completions on other nodes. */}
                <AnimatePresence>
                  {drops.map((drop) => (
                    <LootChestOverlay key={drop.key} />
                  ))}
                </AnimatePresence>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
